import React, { useState, useEffect, useRef } from "react";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import "./AdditionalConsumableList.css";
import { getLocalizedText } from "../../../../utils/localization";
import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { getAddConsumableList, getAllGroups, createConsumable, createGroup } from "../../../../modules/NursignCare/AdditonalConsumable/AddConsumableThunk";
import { resetGetAddConsumableList } from "../../../../modules/NursignCare/AdditonalConsumable/AddConsumableSlice";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import * as XLSX from "xlsx";
// Fetch country list for consumable upload
import { getNationalIdCountries } from "../../../../modules/NationalIdCategory/NationalIdThunk";

const AdditionalConsumableList = () => {
  const dispatch = useDispatch();
  const { addConsumableListSuccess, addConsumableListErr } = useSelector((state) => state.addConsumable);
  const { t, i18n } = useTranslation();
  // Get the list of countries (id + name) from Redux store (nationalId slice)
  const { countries } = useSelector((state) => state.nationalId);
  const nav = useNavigate();
  // Load countries on component mount if not already loaded
  React.useEffect(() => {
    if (!countries || countries.length === 0) {
      dispatch(getNationalIdCountries());
    }
  }, [dispatch, countries]);
  const location = useLocation();
  const breadcrumbs = ["nursingCare", "manageResource"];
  const paths = [strings.navigation.additionalConsumableList, location.pathname];
  const [allConsumables, setAllConsumables] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [clientPage, setClientPage] = useState(1);
  const itemsPerPage = 10;
  const [searchh, setSearch] = useState("");
  const fileInputRef = useRef(null);

  const fetchGetConsumable = () => {
    dispatch(getAddConsumableList({ page: 1, limit: 1000, search: "", searchFields: "itemName,code" }));
  };

  useEffect(() => {
    fetchGetConsumable();
  }, []);

  useEffect(() => {
    setClientPage(1);
  }, [searchh]);

  useEffect(() => {
    if (addConsumableListErr) {
      ToastPopup.error(addConsumableListErr);
    }
    if (addConsumableListSuccess && addConsumableListSuccess.status === "success") {
      setAllConsumables(addConsumableListSuccess.data.consumables || []);
    }
    dispatch(resetGetAddConsumableList());
  }, [addConsumableListSuccess, addConsumableListErr]);

  const filteredConsumables = React.useMemo(() => {
    const q = (searchh || "").trim().toLowerCase();
    if (!q) return allConsumables;
    return allConsumables.filter((item) => {
      const nameEn = (item.itemName?.en || "").toLowerCase();
      const nameVi = (item.itemName?.vi || "").toLowerCase();
      const code = (item.code || "").toLowerCase();
      return nameEn.includes(q) || nameVi.includes(q) || code.includes(q);
    });
  }, [allConsumables, searchh]);

  const totalPages = Math.max(1, Math.ceil(filteredConsumables.length / itemsPerPage));
  const currentPage = clientPage;
  const consumableList = filteredConsumables.slice((clientPage - 1) * itemsPerPage, clientPage * itemsPerPage);

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, { type: "ellipsis" }, totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      } else {
        pages.push(1, { type: "ellipsis" }, currentPage, { type: "ellipsis" }, totalPages);
      }
    }
    return pages;
  };

  const handleClick = (pageNumber) => {
    setClientPage(pageNumber);
  };

  const handlePrevNext = (direction) => {
    if (direction === "prev" && clientPage > 1) {
      setClientPage((p) => p - 1);
    } else if (direction === "next" && clientPage < totalPages) {
      setClientPage((p) => p + 1);
    }
  };

  const handleVerifyClick = (item) => {
    const flattened = {
      ...item,
      ...item.price,
      itemName: JSON.stringify(item.itemName),
      groupName: JSON.stringify(item.group.group),
      groupId: item.group._id,
    };
    delete flattened.price;
    delete flattened.group;
    const queryParams = queryString.stringify(flattened, { encode: true });
    nav(`${strings.navigation.editConsumable}?${queryParams}`);
  };

  const truncateText = (text) => {
    const displayText = getLocalizedText(text, i18n.language);
    if (displayText.length > 15) {
      return displayText.slice(0, 15) + "...";
    }
    return displayText;
  };



  const handleSearch = (query) => {
    setSearch(query);
  };

  const handleBulkUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws);
      processBulkConsumables(data);
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const processBulkConsumables = async (data) => {
    let successCount = 0;
    let failCount = 0;
    let duplicateCount = 0;
    let createdGroupCount = 0;
    ToastPopup.info("Processing bulk upload... please wait.");

    let existingCodes = new Set();
    try {
      const existingResp = await dispatch(
        getAddConsumableList({
          page: 1,
          limit: 1000,
          search: "",
          searchFields: "itemName,code",
        }),
      ).unwrap();
      const existing = existingResp?.data?.consumables || [];
      existing.forEach((c) => {
        if (c.code) existingCodes.add(String(c.code).toUpperCase());
      });
    } catch (e) { }

    const getGroupKey = (group) => {
      const nameEn = (group?.en ?? "").trim().toLowerCase();
      const nameVi = (group?.vi ?? "").trim().toLowerCase();
      return nameEn && nameVi ? `${nameEn}::${nameVi}` : "";
    };

    let groupMap = new Map();
    try {
      const grpResp = await dispatch(getAllGroups()).unwrap();
      const groups = grpResp?.data?.groups || [];
      groups.forEach((g) => {
        const groupKey = getGroupKey(g.group);
        if (groupKey) {
          groupMap.set(groupKey, g._id);
        }
      });
    } catch (e) { }

    const toStr = (val, def = "") => {
      if (val === null || val === undefined) return def;
      const s = String(val).trim();
      return s.length > 0 ? s : def;
    };
    const normalizeUOM = (val) => {
      const s = toStr(val).toUpperCase();
      if (["PCS", "PAIR", "PACK"].includes(s)) return s;
      return s || "PCS";
    };
    const numberize = (val) => {
      const s = toStr(val);
      if (!s) return "";
      const clean = s.replace(/,/g, "");
      return /^\d+(\.\d+)?$/.test(clean) ? clean : "";
    };
  // Helper: finds a column whose header includes the word "group" (case‑insensitive).
  // Utility to fetch a field value from a row given possible header variations.
  function getField(row, keys) {
  // Build a case‑insensitive map of column names to values
  const map = {};
  Object.entries(row).forEach(([k, v]) => {
    if (k != null) {
      map[k.trim().toLowerCase()] = v;
    }
  });
  for (const key of keys) {
    const lookup = key.trim().toLowerCase();
    if (lookup in map) {
      const val = map[lookup];
      if (val != null && String(val).trim() !== "") {
        return String(val).trim();
      }
    }
  }
  return "";
}
  // If none is found, fall back to the first non‑empty cell value.
  const extractGroupName = (row) => {
    // First pass: look for keys containing "group"
    for (const [key, value] of Object.entries(row)) {
      if (/group/i.test(key) && value != null && String(value).trim() !== "") {
        return toStr(value);
      }
    }
    // Fallback: return the first non‑empty string value in the row
    for (const value of Object.values(row)) {
      if (value != null && String(value).trim() !== "") {
        return toStr(value);
      }
    }
    return "";
  };

    for (const row of data) {
      // Use the robust helper to pull the group name from any column variant
      // Attempt to get a group name; if none found, use first existing group as fallback
      const groupObject = {
        en: getField(row, ["Group EN"]),
        vi: getField(row, ["Group VI"]),
      };
      if (!groupObject.en || !groupObject.vi) {
        console.error("Row failed - Missing Group EN or Group VI", groupObject);
        failCount++;
        continue;
      }
      const groupKey = getGroupKey(groupObject);
      let groupId = groupMap.get(groupKey);


      const code = (row["Code"] ?? "").toString().trim().toUpperCase();
      const itemNameEn = (row["Item Name EN"] ?? "").toString().trim();
const itemNameVi = (row["Item Name VI"] ?? "").toString().trim();
      const stockUOM = normalizeUOM((row["Stock UOM"] ?? "").toString().trim());
      const numberOfPcs = numberize(row["Number Of Pcs"]);
      const opdInternationalPrice = numberize(row["OPD International Price"]);
      const opdLocalPrice = numberize(row["OPD Local Price"]);
      const opdSubsidized = numberize(row["OPD Subsidized Price"]);

      if (!groupId) {
        try {
          console.log("Bulk upload group object:", groupObject);
          const cg = await dispatch(createGroup({ body: { group: groupObject } })).unwrap();
          const created =
            cg?.data?.group?._id ||
            cg?.data?._id ||
            cg?.group?._id ||
            cg?._id ||
            null;
          if (created) {
            groupId = created;
            groupMap.set(groupKey, groupId);
            createdGroupCount++;
            console.info(`Created missing group "${groupObject.en}" / "${groupObject.vi}"`);
          } else {
            try {
              const refresh = await dispatch(getAllGroups()).unwrap();
              const groups = refresh?.data?.groups || [];
              const found = groups.find((g) => getGroupKey(g.group) === groupKey);
              if (found?._id) {
                groupId = found._id;
                groupMap.set(groupKey, groupId);
                createdGroupCount++;
                console.info(`Created and resolved group "${groupObject.en}" / "${groupObject.vi}"`);
              }
            } catch (e) { }
          }
        } catch (err) {
          const msg = typeof err === "string" ? err : err?.message || "";
          console.error(`Row failed - Group not found and could not be created: "${groupObject.en}" / "${groupObject.vi}"`, msg);
        }
        if (!groupId) {
          failCount++;
          continue;
        }
      }
// Validation for required fields
      if (!code) {
        console.error("Row failed - Missing Code");
        failCount++;
        continue;
      }
      if (!itemNameEn) {
        console.error("Row failed - Missing Item Name EN");
        failCount++;
        continue;
      }
      if (!itemNameVi) {
        console.error("Row failed - Missing Item Name VI");
        failCount++;
        continue;
      }
      if (!stockUOM) {
        console.error("Row failed - Missing Stock UOM");
        failCount++;
        continue;
      }
      if (!numberOfPcs) {
        console.error("Row failed - Missing Number Of Pcs");
        failCount++;
        continue;
      }
      if (existingCodes.has(code)) {
        duplicateCount++;
        continue;
      }

      const body = (() => {
        // ----- Country handling -----
        const excelCountry = (row["Country"] ?? "").toString().trim();
        if (!excelCountry) {
          console.error("Row failed - Missing Country");
          failCount++;
          return null; // skip this row
        }
        const matchedCountry = countries?.find(
          (c) => c.name?.toLowerCase() === excelCountry.toLowerCase()
        );
        if (!matchedCountry) {
          console.error(`Invalid country: ${excelCountry}`);
          failCount++;
          return null; // skip this row
        }
        const countryId = matchedCountry._id;
        // Build final payload
        return {
          group: groupId,
          code,
          itemName: { en: itemNameEn, vi: itemNameVi },
          stockUOM,
          numberOfPcs,
          price: {
            opdInternationalPrice,
            opdLocalPrice,
            opdSubsidized,
          },
          country: countryId,
        };
      })();

      if (!body) {
        // payload creation failed due to validation – skip API call
        continue;
      }

      // Debug logging of final payload
      console.log("Final bulk consumable payload:", body);

      try {
        const result = await dispatch(createConsumable({ body })).unwrap();
        if (result?.status === "success") {
          successCount++;
          existingCodes.add(code);
        } else {
          console.error("Row failed - API error:", result);
          failCount++;
        }
      } catch (error) {
        const msg = typeof error === "string" ? error : error?.message || "";
        if (msg && /exist/i.test(msg)) {
          duplicateCount++;
        } else {
          console.error("Row failed - Exception:", error);
          failCount++;
        }
      }
    }

    const total = data.length;
    if (successCount > 0) {
      ToastPopup.success(`Added ${successCount} of ${total}.`);
    }
    if (duplicateCount > 0) {
      ToastPopup.info(`Skipped ${duplicateCount} duplicates. Executed the rest.`);
    }
    if (failCount > 0) {
      ToastPopup.error(`Failed ${failCount} rows. Check console for details.`);
    }
    if (createdGroupCount > 0) {
      ToastPopup.info(`Created ${createdGroupCount} missing group(s) from the sheet.`);
    }
    if (successCount > 0 || (successCount + duplicateCount + failCount > 0)) {
      fetchGetConsumable();
    }
  };

  return (
    <div className="dp-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading">{t("additionalConsumables")}</div>
      <div className="department-search-line">
        <Widgets.SearchBox onSearch={handleSearch} placeholder={"searchByCodeName"} />
        <input type="file" accept=".xlsx, .xls" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
        <div className="add-consumable-group">
          <Widgets.IconButton iconSrc={Icons.publish} buttonText={t("BulkUpload")} onClick={handleBulkUploadClick} />
          <Widgets.IconButton iconSrc={Icons.plusIconGreen} buttonText="createConsumable" onClick={() => nav(strings.navigation.addConsumable)} />
        </div>
      </div>
      <div className="dp-list-box">
        <div className="dp-secondary-box">
          <div className="dp-price-box">
            <table className="dp-price-layout">
              <thead className="dp-thead">
                <tr>
                  <th>{t("name")}</th>
                  <th>{t("code")}</th>
                  <th>{t("stockUOM")}</th>
                  <th>{t("groupName")}</th>
                  <th>{t("opdIntInsured")}</th>
                  <th>{t("opdLocalInsured")}</th>
                  <th>{t("opdSubsidized")}</th>
                </tr>
              </thead>
              <tbody className="dp-tbody">
                {consumableList.length > 0 &&
                  consumableList.map((item, index) => (
                    <tr key={index} className="acl-table-row" onClick={() => handleVerifyClick(item)}>
                      <td className="acl-name-list" onMouseEnter={() => setHoveredIndex(index)} onMouseLeave={() => setHoveredIndex(null)}>
                        {truncateText(item.itemName)}
                        {hoveredIndex === index && <div className="acl-name-list-hover">{getLocalizedText(item.itemName, i18n.language)}</div>}
                      </td>
                      <td>{item.code}</td>
                      <td>{item.stockUOM}</td>
                      <td>{getLocalizedText(item?.group?.group, i18n.language)}</td>
                      <td>{item.price?.opdInternationalPrice}</td>
                      <td>{item.price?.opdLocalPrice}</td>
                      <td>{item.price?.opdSubsidized}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {totalPages === 0 && (
              <div className="no-record-container">
                <div className="no-record-icon">
                  <img src={Icons.NoData} alt="No Data Found" />
                </div>
                <div className="no-record-text">{t("noRecordFound")}</div>
              </div>
            )}
          </div>
          <div className="department-pagination-list">
            {totalPages > 0 && (
              <>
                <button onClick={() => handlePrevNext("prev")} disabled={currentPage === 1} className="department-pagination-prev-list">
                  <img src={Icons.leftArrow} alt="<" />
                </button>
                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && handleClick(page)}
                    disabled={page === currentPage || page.type === "ellipsis"}
                    className={`department-pagination-btn-list ${page === currentPage ? "active-list" : ""} ${page.type === "ellipsis" ? "ellipse" : ""}`}
                  >
                    {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
                  </button>
                ))}
                <button onClick={() => handlePrevNext("next")} disabled={currentPage === totalPages} className="department-pagination-next-list">
                  <img src={Icons.rightArrow} alt=">" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalConsumableList;
